import React, { useState, useEffect, useRef, Fragment } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import Pagination from "react-pagination-js";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";

// ─── Icon primitives ─────────────────────────────────────────────────────────
const Icon = ({ d, size = 16, ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {Array.isArray(d) ? (
      d.map((p, i) => <path key={i} d={p} />)
    ) : (
      <path d={d} />
    )}
  </svg>
);

const CheckIcon = ({ size = 10 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SpinIcon = () => (
  <svg
    className="animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const isShipDatePassed = (shipDate) => {
  if (!shipDate) return false;
  const ship = new Date(shipDate);
  const today = new Date();
  ship.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return ship < today;
};

/**
 * Derives all partial-payment fields from a raw invoice object.
 * Single source of truth — call once per render, pass result down.
 */
const getPartialInfo = (invoice) => {
  if (invoice?.invoiceType !== "partial") return { isPartial: false };

  const partialPaid = !!invoice?.partialPaid;
  const partialMode = invoice?.partialMode || "percentage";
  const partialValue = Number(invoice?.partialValue ?? 0);
  const totalAmount = Number(
    invoice?.totalAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const minimumPartialAmt = Number(invoice?.minimumPartialAmount ?? 0);
  const remainingAmount = Number(
    invoice?.remainingAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const paidAmount = Number(invoice?.paidAmount ?? 0);

  // What does the user owe RIGHT NOW?
  //  • Before partial paid  → minimumPartialAmt
  //  • After partial paid   → remainingAmount (the leftover)
  const amountToPay = partialPaid ? remainingAmount : minimumPartialAmt;

  return {
    isPartial: true,
    partialPaid,
    partialMode,
    partialValue,
    totalAmount,
    minimumPartialAmt,
    remainingAmount,
    paidAmount,
    amountToPay,
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ amountDue }) => {
  const isPaid = Number(amountDue) === 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm font-medium ${isPaid ? "text-[#008236]" : "text-amber-700"}`}
    >
      <span
        className={`size-1.5 rounded-full ${isPaid ? "bg-[#0a7822a6]" : "bg-amber-500"}`}
      />
      {isPaid ? "Paid" : "Due"}
    </span>
  );
};

const StatCard = ({ icon, label, value, bg }) => (
  <div
    className={`${label == "Paid Invoices" ? "max-sm:col-span-2 max-sm:!flex-nowrap" : ""} border border-gray-200 rounded-2xl bg-white shadow-md px-4 sm:px-5 py-4 flex max-lg:flex-wrap sm:items-center gap-2 sm:gap-4`}
  >
    <div
      className={`min-w-[40px] min-w-[40px] lg:w-[70px] w-[40px] h-[40px] lg:h-[70px] rounded-xl ${bg} flex items-center justify-center shrink-0`}
    >
      {icon}
    </div>
    <div className="sm:space-y-1 max-lg:w-full">
      <p className="text-lg lg:text-2xl font-semibold text-[#3A3A3A]">
        {value}
      </p>
      <p className="max-lg:text-sm text-md text-gray-600 font-medium">
        {label}
      </p>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function QuickBookInvoiceList({
  quotesToken,
  quotesCompanyId,
  metaId,
}) {
  const user = useSelector((state) => state.user);
  const history = useNavigate();

  const isConnected = quotesToken || !!user?.quick_book_accesToken;

  const [allInvoices, setAllInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState({});
  const [paymentError, setPaymentError] = useState({});
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // ── Debounce search ──
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination((p) => ({ ...p, page: 1 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (statusFilter !== "") setPagination((p) => ({ ...p, page: 1 }));
  }, [statusFilter]);

  // ── Email helper ──
  const emailParam = user?.email || (quotesToken ? "nandini@yopmail.com" : "");

  // ── Fetch all for stats ──
  const fetchAllInvoicesForStats = async () => {
    if (!isConnected) return;
    try {
      const params = new URLSearchParams({ limit: 1000, email: emailParam });
      const res = await ApiClient.get(
        `quickbooks-docs/invoices/list?${params}`,
      );
      if (res?.success) setAllInvoices(res?.data || []);
    } catch (err) {
      console.error("Error fetching all invoices:", err);
    }
  };

  // ── Fetch paginated page ──
  const fetchInvoices = async () => {
    if (!isConnected) return;
    setLoading(true);
    loader(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        email: emailParam,
      });
      if (statusFilter) params.append("status", statusFilter);
      if (debouncedSearch) params.append("search", debouncedSearch);

      const res = await ApiClient.get(
        `quickbooks-docs/invoices/list?${params}`,
      );
      if (res?.success) {
        setInvoices(res?.data || []);
        setPagination((p) => ({
          ...p,
          total: res?.pagination?.total || 0,
          totalPages: res?.pagination?.totalPages || 0,
        }));
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
      loader(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchAllInvoicesForStats();
      fetchInvoices();
    }
  }, [isConnected]);
  useEffect(() => {
    if (isConnected) fetchInvoices();
  }, [pagination.page, pagination.limit, statusFilter, debouncedSearch]);

  // ── metaId → open modal ──
  useEffect(() => {
    if (!metaId || invoices.length === 0) return;
    const found = invoices.find(
      (i) => i.invoiceId === metaId || i.id === metaId || i._id === metaId,
    );
    if (found) {
      setSelectedInvoice(found);
      setIsViewModalOpen(true);
    }
  }, [metaId, invoices]);

  // ── Stats ──
  const dueCount = allInvoices.filter(
    (i) => Number(i.totalAmountDue || i.amountDue || 0) > 0,
  ).length;
  const totalAmountDue = allInvoices.reduce(
    (s, i) => s + Number(i.totalAmountDue || i.amountDue || 0),
    0,
  );
  const paidCount = allInvoices.filter(
    (i) => Number(i.totalAmountDue || i.amountDue || 0) === 0,
  ).length;

  // ── Handlers ──
  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedInvoice(null);
    const sp = new URLSearchParams(window.location.search);
    sp.delete("id");
    history(`${window.location.pathname}?${sp.toString()}`, { replace: true });
  };

  const handlePayment = async (invoice) => {
    const p = getPartialInfo(invoice);
    const amount = p.isPartial
      ? p.amountToPay
      : invoice.totalAmountDue || invoice.amountDue || 0;

    setPaymentLoading((prev) => ({ ...prev, [invoice.invoiceId]: true }));
    setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" }));

    const payload = {
      name: invoice.contact?.Name || user?.name || "Customer",
      email:
        invoice.BillEmail?.Address ||
        invoice.BillEmail ||
        user?.email ||
        "customer@example.com",
      mobileNo:
        invoice.contact?.Mobile || invoice.contact?.Phone || user?.phone || "",
      invoiceId: invoice.invoiceId,
      quoteId: invoice.EstimateId || "",
      amount: amount?.toString() || "0",
      CustomerRef: invoice.CustomerRef || invoice.customerId || "",
    };

    try {
      const res = await ApiClient.post(
        "quickbook/invoice/payment/link",
        payload,
      );
      if (res?.success && res?.data) {
        setIsViewModalOpen(false);
        setSelectedInvoice(null);
        window.open(res?.data?.paymentUrl, "_blank");
      } else {
        setPaymentError((prev) => ({
          ...prev,
          [invoice.invoiceId]:
            res?.message || "Failed to generate payment link",
        }));
        setTimeout(
          () =>
            setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" })),
          3000,
        );
      }
    } catch {
      setPaymentError((prev) => ({
        ...prev,
        [invoice.invoiceId]: "Something went wrong. Please try again.",
      }));
      setTimeout(
        () => setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" })),
        3000,
      );
    } finally {
      setPaymentLoading((prev) => ({ ...prev, [invoice.invoiceId]: false }));
    }
  };

  const STATUS_TABS = [
    { label: "All", value: "", count: allInvoices.length },
    { label: "Due", value: "due", count: dueCount },
    { label: "Paid", value: "paid", count: paidCount },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mx-auto">
        {/* ── Stats ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Total Invoices"
            value={allInvoices.length}
            bg="bg-[#115B9D17]"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[20px] lg:w-[30px]"
                viewBox="0 0 38 38"
                fill="none"
              >
                <path
                  d="M33.1465 9.23222L24.2404 0.326191C24.0326 0.11823 23.7488 0 23.4531 0H7.86719C6.0256 0 4.52734 1.49825 4.52734 3.33984V34.6602C4.52734 36.5017 6.0256 38 7.86719 38H30.1328C31.9744 38 33.4727 36.5017 33.4727 34.6602V10.0195C33.4727 9.71598 33.3452 9.4309 33.1465 9.23222ZM24.5664 3.80096L29.6717 8.90625H25.6797C25.0658 8.90625 24.5664 8.40683 24.5664 7.79297V3.80096ZM30.1328 35.7734H7.86719C7.25332 35.7734 6.75391 35.274 6.75391 34.6602V3.33984C6.75391 2.72598 7.25332 2.22656 7.86719 2.22656H22.3398V7.79297C22.3398 9.63456 23.8381 11.1328 25.6797 11.1328H31.2461V34.6602C31.2461 35.274 30.7467 35.7734 30.1328 35.7734Z"
                  fill="#115B9D"
                />
                <path
                  d="M25.6797 15.7344H12.3203C11.7055 15.7344 11.207 16.2328 11.207 16.8477C11.207 17.4625 11.7055 17.9609 12.3203 17.9609H25.6797C26.2945 17.9609 26.793 17.4625 26.793 16.8477C26.793 16.2328 26.2945 15.7344 25.6797 15.7344Z"
                  fill="#115B9D"
                />
                <path
                  d="M25.6797 20.1875H12.3203C11.7055 20.1875 11.207 20.686 11.207 21.3008C11.207 21.9156 11.7055 22.4141 12.3203 22.4141H25.6797C26.2945 22.4141 26.793 21.9156 26.793 21.3008C26.793 20.686 26.2945 20.1875 25.6797 20.1875Z"
                  fill="#115B9D"
                />
                <path
                  d="M25.6797 24.6406H12.3203C11.7055 24.6406 11.207 25.1391 11.207 25.7539C11.207 26.3687 11.7055 26.8672 12.3203 26.8672H25.6797C26.2945 26.8672 26.793 26.3687 26.793 25.7539C26.793 25.1391 26.2945 24.6406 25.6797 24.6406Z"
                  fill="#115B9D"
                />
                <path
                  d="M21.2266 29.0938H12.3203C11.7055 29.0938 11.207 29.5922 11.207 30.207C11.207 30.8219 11.7055 31.3203 12.3203 31.3203H21.2266C21.8414 31.3203 22.3398 30.8219 22.3398 30.207C22.3398 29.5922 21.8414 29.0938 21.2266 29.0938Z"
                  fill="#115B9D"
                />
              </svg>
            }
          />
          <StatCard
            label="Amount Due"
            value={formatCurrency(totalAmountDue)}
            bg="bg-amber-50"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[20px] lg:w-[30px]"
                viewBox="0 0 39 39"
                fill="none"
              >
                <path
                  d="M19.5 1.21875C9.41798 1.21875 1.21875 9.41798 1.21875 19.5C1.21875 29.582 9.41798 37.7812 19.5 37.7812C29.582 37.7812 37.7812 29.582 37.7812 19.5C37.7812 9.41798 29.582 1.21875 19.5 1.21875ZM19.5 3.65625C28.2647 3.65625 35.3533 10.7353 35.3533 19.5C35.3533 28.2647 28.2647 35.3533 19.5 35.3533C10.7353 35.3533 3.65625 28.2647 3.65625 19.5C3.65625 10.7353 10.7353 3.65625 19.5 3.65625ZM19.4833 9.75714C19.1614 9.76281 18.8547 9.89567 18.6304 10.1267C18.406 10.3577 18.2822 10.6682 18.286 10.9902V12.0161C16.3182 12.3853 14.6345 13.6525 14.6345 15.8485C14.6345 17.3719 15.3213 18.6041 16.2437 19.3215C17.166 20.0389 18.2075 20.3569 19.1215 20.6616C20.0356 20.9663 20.8246 21.2576 21.2734 21.6066C21.7221 21.9556 21.9375 22.2493 21.9375 23.1634C21.9375 25.1946 17.072 25.1946 17.072 23.1634C17.0756 23.0011 17.0468 22.8397 16.9871 22.6886C16.9275 22.5376 16.8383 22.4 16.7248 22.284C16.6112 22.1679 16.4757 22.0757 16.326 22.0127C16.1764 21.9497 16.0156 21.9173 15.8533 21.9173C15.6909 21.9173 15.5302 21.9497 15.3805 22.0127C15.2309 22.0757 15.0953 22.1679 14.9818 22.284C14.8682 22.4 14.779 22.5376 14.7194 22.6886C14.6598 22.8397 14.6309 23.0011 14.6345 23.1634C14.6345 25.3577 16.3196 26.6273 18.286 26.9982V28.0193C18.2824 28.1817 18.3113 28.3431 18.3709 28.4941C18.4305 28.6451 18.5197 28.7827 18.6333 28.8988C18.7468 29.0148 18.8824 29.1071 19.032 29.17C19.1817 29.233 19.3424 29.2654 19.5048 29.2654C19.6671 29.2654 19.8278 29.233 19.9775 29.17C20.1272 29.1071 20.2627 29.0148 20.3763 28.8988C20.4898 28.7827 20.579 28.6451 20.6386 28.4941C20.6982 28.3431 20.7271 28.1817 20.7235 28.0193V26.9982C22.6902 26.6275 24.3774 25.358 24.3774 23.1634C24.3774 21.64 23.6882 20.4078 22.7659 19.6904C21.8435 18.973 20.8021 18.6526 19.888 18.3479C18.9739 18.0432 18.1849 17.7543 17.7361 17.4053C17.2874 17.0563 17.072 16.7626 17.072 15.8485C17.072 13.8173 21.9375 13.8173 21.9375 15.8485C21.9375 16.1721 22.066 16.4824 22.2948 16.7111C22.5236 16.9399 22.8339 17.0684 23.1574 17.0684C23.481 17.0684 23.7913 16.9399 24.0201 16.7111C24.2488 16.4824 24.3774 16.1721 24.3774 15.8485C24.3774 13.6558 22.6896 12.3898 20.7235 12.0185V10.9902C20.7254 10.8271 20.6946 10.6653 20.6329 10.5144C20.5711 10.3635 20.4797 10.2264 20.3641 10.1115C20.2485 9.99649 20.1109 9.90589 19.9596 9.84503C19.8083 9.78417 19.6464 9.75428 19.4833 9.75714Z"
                  fill="#E5A150"
                />
              </svg>
            }
          />
          <StatCard
            label="Paid Invoices"
            value={paidCount}
            bg="bg-[#F0FEF4]"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[20px] lg:w-[30px]"
                viewBox="0 0 33 33"
                fill="none"
              >
                <g clipPath="url(#clip0_162_304)">
                  <path
                    d="M32.0569 4.51129C31.5715 4.02499 30.7838 4.02416 30.2983 4.50923L15.3779 19.3903L9.99462 13.5436C9.52946 13.0386 8.743 13.0059 8.23724 13.471C7.73186 13.9361 7.69951 14.723 8.16466 15.2284L14.4247 22.0269C14.654 22.2761 14.9748 22.4208 15.3132 22.4278C15.3222 22.4282 15.331 22.4282 15.3397 22.4282C15.6685 22.4282 15.9848 22.2976 16.2178 22.0655L32.0545 6.27028C32.5412 5.78527 32.542 4.99759 32.0569 4.51129Z"
                    fill="#138C45"
                  />
                  <path
                    d="M31.7562 15.2562C31.0693 15.2562 30.5126 15.813 30.5126 16.5C30.5126 24.2268 24.2268 30.5126 16.5 30.5126C8.77362 30.5126 2.48744 24.2268 2.48744 16.5C2.48744 8.77362 8.77362 2.48744 16.5 2.48744C17.1869 2.48744 17.7438 1.93069 17.7438 1.24375C17.7438 0.556746 17.1869 0 16.5 0C7.4018 0 0 7.4018 0 16.5C0 25.5978 7.4018 33 16.5 33C25.5978 33 33 25.5978 33 16.5C33 15.8131 32.4433 15.2562 31.7562 15.2562Z"
                    fill="#138C45"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_162_304">
                    <rect width="33" height="33" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          />
        </div>

        {/* ── Filters ── */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col md:flex-row items-start sm:items-center justify-between gap-2">
            {/* Tabs */}
            <div className="w-full lg:w-fit max-lg:overflow-auto flex items-center gap-2 bg-[#ECF2F7] rounded-full max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-2">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setStatusFilter(tab.value)}
                  className={`whitespace-nowrap px-3 py-2 rounded-full max-xl:text-sm text-md font-medium transition-all flex items-center gap-2 ${
                    statusFilter === tab.value
                      ? "bg-[#115B9D] text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`text-[10px] sm:text-xs font-semibold px-[2px] min-w-[21px] sm:min-w-[25px] h-[21px] sm:h-[25px] flex items-center justify-center rounded-full ${
                      statusFilter === tab.value
                        ? "bg-white text-[#115B9D]"
                        : "bg-[#D8E4EF] text-[#7C7C7C]"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full">
              <svg
                className="absolute left-5 top-[28px] -translate-y-1/2 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, invoice #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-full pl-11 pr-12 py-4 text-md text-gray-800 outline-none bg-[#F8F8F8] border-[#DADADA] transition-all"
              />
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#115B9D]" />
          </div>
        ) : (
          <div>
            <div className="sm:hidden space-y-4">
              {invoices.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-gray-400"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500">No invoices found</p>
                  </div>
                </div>
              ) : (
                invoices.map((item, index) => {
                  const amountDueValue =
                    item.totalAmountDue || item.amountDue || 0;

                  const isPaid = Number(amountDueValue) === 0;
                  const p = getPartialInfo(item);
                  const shipPassed = isShipDatePassed(item?.shipDate);

                  return (
                    <div
                      key={item.invoiceId || item._id || index}
                      className={`bg-white border rounded-xl p-5 shadow-sm space-y-4 ${
                        shipPassed
                          ? "border-red-200 bg-red-50/30"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">
                            Invoice #
                          </p>

                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm font-semibold text-[#115B9D]">
                              #{item.invoiceNumber}
                            </p>

                            {p.isPartial && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                Partial
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => history(`/invoice/detail/${item._id}`)}
                          className="size-9 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-[#115B9D] flex items-center justify-center transition"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>

                      {/* Status + Dates */}
                      <div className="grid grid-cols-2 gap-3 border-y border-gray-100 py-3">
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <StatusBadge amountDue={amountDueValue} />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Created</p>
                          <p className="text-sm text-gray-700">
                            {formatDate(item.createdAtQb || item.createdAt)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Due Date</p>
                          <p className="text-sm text-gray-700">
                            {formatDate(item.dueDate)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Ship Date</p>
                          <p
                            className={`text-sm ${
                              shipPassed
                                ? "text-red-500 font-medium"
                                : "text-gray-700"
                            }`}
                          >
                            {formatDate(item.shipDate)}
                          </p>
                        </div>
                      </div>

                      {/* Tax + Amount */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500">Total Tax</p>
                          <p className="text-sm text-gray-700">
                            {formatCurrency(item.totalTax, item.currencyCode)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Amount Due</p>

                          {p.isPartial && !isPaid ? (
                            <>
                              <p className="text-sm font-semibold text-amber-600">
                                {formatCurrency(
                                  p.amountToPay,
                                  item.currencyCode,
                                )}
                              </p>

                              <p className="text-[11px] text-gray-500">
                                Total:{" "}
                                {formatCurrency(
                                  p.totalAmount,
                                  item.currencyCode,
                                )}
                              </p>
                            </>
                          ) : (
                            <p
                              className={`text-sm font-semibold ${
                                isPaid ? "text-green-600" : "text-amber-600"
                              }`}
                            >
                              {formatCurrency(
                                amountDueValue,
                                item.currencyCode,
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        {!isPaid && (
                          <button
                            onClick={() => handlePayment(item)}
                            disabled={paymentLoading[item.invoiceId]}
                            className="flex-1 py-2 rounded-lg bg-[#16a34a] hover:bg-[#15803d] text-white border border-green-200 text-sm font-semibold transition-all active:scale-[0.98]"
                          >
                            {paymentLoading[item.invoiceId]
                              ? "Processing..."
                              : "Pay Now"}
                          </button>
                        )}
                      </div>

                      {paymentError[item.invoiceId] && (
                        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                          {paymentError[item.invoiceId]}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="max-sm:!hidden">
              <div className="overflow-auto border border-gray-200 rounded-xl">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {[
                        "Actions",
                        "Invoice",
                        "Created",
                        "Due Date",
                        "Ship Date",
                        "Tax",
                        "Amount",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          className="text-left py-3.5 px-4 font-semibold text-sm text-gray-800"
                        >
                          <span className="text-[13px] lg:text-sm text-gray-800 font-semibold uppercase whitespace-nowrap">
                            {h}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.length === 0 ? (
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td colSpan={8} className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-gray-400"
                              >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-500">
                              No invoices found
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      invoices.map((item, index) => {
                        const amountDueValue =
                          item.totalAmountDue || item.amountDue || 0;
                        const isPaid = Number(amountDueValue) === 0;
                        const p = getPartialInfo(item);
                        const shipPassed = isShipDatePassed(item?.shipDate);

                        return (
                          <tr
                            key={item.invoiceId || item._id || index}
                            className={`transition-colors ${shipPassed ? "bg-red-50/60" : "hover:bg-gray-50/50"}`}
                          >
                            {/* Actions */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    history(`/invoice/detail/${item._id}`)
                                  }
                                  title="View Details"
                                  className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#115B9D] transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                </button>

                                {!isPaid && (
                                  <div className="relative">
                                    <button
                                      type="button"
                                      onClick={() => handlePayment(item)}
                                      disabled={paymentLoading[item.invoiceId]}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap"
                                    >
                                      {paymentLoading[item.invoiceId] ? (
                                        <>
                                          <SpinIcon /> Processing…
                                        </>
                                      ) : (
                                        <>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <rect
                                              x="1"
                                              y="11"
                                              width="22"
                                              height="11"
                                              rx="2"
                                              ry="2"
                                            />
                                            <path d="M1 11V8a2 2 0 0 1 2-2h4l2-4 2 4h4a2 2 0 0 1 2 2v3" />
                                          </svg>
                                          Pay{" "}
                                          {p.isPartial
                                            ? formatCurrency(
                                                p.amountToPay,
                                                item.currencyCode,
                                              )
                                            : formatCurrency(
                                                amountDueValue,
                                                item.currencyCode,
                                              )}
                                        </>
                                      )}
                                    </button>
                                    {paymentError[item.invoiceId] && (
                                      <div className="absolute top-full left-0 mt-2 z-10 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 whitespace-nowrap shadow-sm">
                                        {paymentError[item.invoiceId]}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* Invoice # + partial badge */}
                            <td className="py-3.5 px-4">
                              <div className="flex flex-col gap-1">
                                <span className="max-lg:text-sm text-md font-semibold text-[#115B9D]">
                                  #{item.invoiceNumber}
                                </span>
                                {p.isPartial && (
                                  <span className="text-[12px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 w-fit leading-tight">
                                    {p.partialPaid
                                      ? "Partial paid"
                                      : `Partial · ${p.partialMode === "percentage" ? `${p.partialValue}% upfront` : "Fixed upfront"}`}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-4 max-lg:text-sm text-md text-md text-[#686666] whitespace-nowrap">
                              {formatDate(item.createdAtQb || item.createdAt)}
                            </td>
                            <td className="py-3.5 px-4 max-lg:text-sm text-md text-md text-[#686666] whitespace-nowrap">
                              {formatDate(item.dueDate)}
                            </td>
                            <td className="py-3.5 px-4">
                              <span
                                className={`max-lg:text-sm text-md whitespace-nowrap ${shipPassed ? "text-red-500 font-medium" : "text-[#7C7C7C]"}`}
                              >
                                {formatDate(item.shipDate)}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 max-lg:text-sm text-md text-[#686666] whitespace-nowrap">
                              {formatCurrency(item.totalTax, item.currencyCode)}
                            </td>
                            {/* Amount column */}
                            <td className="py-3.5 px-4">
                              {p.isPartial && !isPaid ? (
                                <div className="flex flex-col gap-0.5">
                                  <p className="max-lg:text-sm text-md font-semibold text-amber-600 whitespace-nowrap">
                                    {formatCurrency(
                                      p.amountToPay,
                                      item.currencyCode,
                                    )}
                                    <span className="text-[10px] font-normal text-[#7C7C7C] ml-1">
                                      {p.partialPaid ? "remaining" : "due now"}
                                    </span>
                                  </p>
                                  <p className="text-[13px] text-[#7C7C7C]">
                                    Total:{" "}
                                    {formatCurrency(
                                      p.totalAmount,
                                      item.currencyCode,
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <p
                                  className={`max-lg:text-sm text-md font-semibold whitespace-nowrap ${isPaid ? "text-[#008236]" : "text-amber-600"}`}
                                >
                                  {formatCurrency(
                                    amountDueValue,
                                    item.currencyCode,
                                  )}
                                </p>
                              )}
                            </td>
                            <td className="py-3.5 px-4">
                              <StatusBadge amountDue={amountDueValue} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Pagination ── */}
        {pagination.total > pagination.limit && (
          <div className="mt-5 flex flex-col gap-4 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total} entries
            </p>
            <div className="custom-pagination">
              <Pagination
                currentPage={pagination.page}
                totalSize={pagination.total}
                sizePerPage={pagination.limit}
                changeCurrentPage={handlePageChange}
                theme="border-bottom"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Invoice Detail Modal ─────────────────────────────────────────────── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeModal}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
                  {/* Close */}
                  <div className="px-2 py-2">
                    <button
                      onClick={closeModal}
                      className="z-10 min-w-[35px] w-[35px] h-[35px] ml-auto bg-white rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="overflow-y-auto scrollbar flex-1">
                    {/* ── Header ── */}
                    <div className="px-6 pb-5 text-center">
                      <div className="w-12 h-12 bg-[#115B9D] rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-2xl font-bold tracking-wide">
                        Invoice
                      </Dialog.Title>
                      <p className="mt-1 text-[11px] font-semibold tracking-[2px] text-[#115B9D] uppercase">
                        #{selectedInvoice?.invoiceNumber}
                      </p>
                      {selectedInvoice && (
                        <div className="mt-4">
                          <p className="text-sm">
                            Hello,{" "}
                            <span className="font-bold capitalize">
                              {selectedInvoice.contact?.Name || "Customer"}
                            </span>{" "}
                            👋
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Here are your invoice details. Use the pay button
                            below to settle your balance.
                          </p>
                        </div>
                      )}
                    </div>

                    {selectedInvoice &&
                      (() => {
                        const amountDueRaw = Number(
                          selectedInvoice?.totalAmountDue ||
                            selectedInvoice?.amountDue ||
                            0,
                        );
                        const isPaid = amountDueRaw === 0;
                        const p = getPartialInfo(selectedInvoice);

                        // ── Summary math (invoice-only data, no estimate bleed) ──
                        const invoiceLines =
                          selectedInvoice?.estimate?.raw?.Line ||
                          selectedInvoice?.raw?.Line ||
                          [];
                        const salesLines = invoiceLines.filter(
                          (l) => l.DetailType === "SalesItemLineDetail",
                        );
                        const discountLine = invoiceLines.find(
                          (item) => item?.DetailType === "DiscountLineDetail",
                        );
                        const discount = Number(
                          discountLine?.Amount || discountLine?.LineAmount || 0,
                        );
                        const subtotal = salesLines.reduce(
                          (s, l) => s + Number(l.Amount || 0),
                          0,
                        );
                        const tax = Number(selectedInvoice?.totalTax || 0);
                        const totalInvoice = Number(
                          selectedInvoice?.totalAmount ||
                            selectedInvoice?.raw?.TotalAmt ||
                            subtotal + tax,
                        );

                        return (
                          <>
                            {/* ── Dates + Status Bar ── */}
                            <div className="px-5 pb-4 border-b border-gray-100">
                              <div className="border border-gray-200 rounded-xl grid grid-cols-4 items-stretch bg-[#115B9D08]">
                                {[
                                  {
                                    label: "Created",
                                    value: formatDate(
                                      selectedInvoice.createdAtQb ||
                                        selectedInvoice.createdAt,
                                    ),
                                    color: "text-gray-800",
                                  },
                                  {
                                    label: "Due Date",
                                    value: formatDate(selectedInvoice.dueDate),
                                    color: "text-red-500",
                                  },
                                  {
                                    label: "Ship Date",
                                    value: formatDate(selectedInvoice.shipDate),
                                    color: isShipDatePassed(
                                      selectedInvoice.shipDate,
                                    )
                                      ? "text-red-500"
                                      : "text-gray-800",
                                  },
                                ].map((item, i, arr) => (
                                  <div
                                    key={item.label}
                                    className={`py-4 px-3 ${i < arr.length ? "border-r border-gray-200" : ""}`}
                                  >
                                    <p className="text-[9px] font-semibold tracking-widest text-gray-400 uppercase mb-1">
                                      {item.label}
                                    </p>
                                    <p
                                      className={`text-sm font-bold ${item.color}`}
                                    >
                                      {item.value}
                                    </p>
                                  </div>
                                ))}
                                <div className="py-4 px-3 flex items-center justify-center">
                                  <StatusBadge amountDue={amountDueRaw} />
                                </div>
                              </div>
                            </div>

                            {/* ── Partial Payment Flow (only for partial+unpaid) ── */}
                            {p.isPartial && !isPaid && (
                              <div className="px-5 py-4 border-b border-gray-100">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                                  Payment Schedule
                                </p>

                                {/* Explanation banner */}
                                <div className="mb-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 leading-relaxed">
                                  <span className="font-semibold">
                                    How this works:
                                  </span>{" "}
                                  This invoice uses a two-step partial payment
                                  plan.
                                  {p.partialMode === "percentage"
                                    ? ` Pay ${p.partialValue}% upfront (${formatCurrency(p.minimumPartialAmt)}), then the remaining ${formatCurrency(p.remainingAmount - p.minimumPartialAmt > 0 ? p.remainingAmount : p.totalAmount - p.minimumPartialAmt)} after.`
                                    : ` Pay the fixed partial amount (${formatCurrency(p.minimumPartialAmt)}) upfront, then the remaining balance after.`}
                                </div>

                                {/* Step 1 */}
                                <div
                                  className={`flex items-start gap-3 p-3.5 rounded-t-xl border border-b-0 ${p.partialPaid ? "bg-[#F0FEF4] border-green-200" : "bg-amber-50 border-amber-200"}`}
                                >
                                  <div
                                    className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${p.partialPaid ? "bg-[#008236]" : "bg-amber-500"}`}
                                  >
                                    {p.partialPaid ? <CheckIcon /> : "1"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <p
                                        className={`text-sm font-semibold ${p.partialPaid ? "text-[#008236]" : "text-amber-700"}`}
                                      >
                                        {p.partialPaid
                                          ? "Partial payment received ✓"
                                          : "Step 1 — Pay partial amount"}
                                      </p>
                                      <p
                                        className={`text-sm font-bold shrink-0 ${p.partialPaid ? "text-[#008236]" : "text-amber-700"}`}
                                      >
                                        {formatCurrency(p.minimumPartialAmt)}
                                      </p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {p.partialMode === "percentage"
                                        ? `${p.partialValue}% of total invoice ${formatCurrency(p.totalAmount)}`
                                        : "Fixed partial amount as agreed"}
                                    </p>
                                  </div>
                                </div>

                                {/* Step 2 */}
                                <div
                                  className={`flex items-start gap-3 p-3.5 rounded-b-xl border ${p.partialPaid ? "bg-[#EFF6FF] border-blue-200" : "bg-gray-50 border-gray-200"}`}
                                >
                                  <div
                                    className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${p.partialPaid ? "bg-[#115B9D]" : "bg-gray-300"}`}
                                  >
                                    2
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <p
                                        className={`text-sm font-semibold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                                      >
                                        {p.partialPaid
                                          ? "Step 2 — Pay remaining balance"
                                          : "Remaining balance (unlocks after Step 1)"}
                                      </p>
                                      <p
                                        className={`text-sm font-bold shrink-0 ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                                      >
                                        {formatCurrency(
                                          p.totalAmount - p.minimumPartialAmt,
                                        )}
                                      </p>
                                    </div>
                                    <p
                                      className={`text-xs mt-0.5 ${p.partialPaid ? "text-gray-500" : "text-gray-400"}`}
                                    >
                                      {p.partialPaid
                                        ? "Pay now to fully clear this invoice."
                                        : "Complete Step 1 first to unlock this payment."}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ── Invoice Items ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5">
                                Invoice Items
                              </p>
                              <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-[#115B9D]">
                                    <tr>
                                      <th className="py-2.5 px-4 text-left text-[10px] font-semibold text-white tracking-wide">
                                        #
                                      </th>
                                      <th className="py-2.5 px-4 text-left text-[10px] font-semibold text-white tracking-wide">
                                        Description
                                      </th>
                                      <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                                        Qty
                                      </th>
                                      <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                                        Unit Price
                                      </th>
                                      <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                                        Amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {salesLines.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="py-4 px-4 text-center text-sm text-gray-400"
                                        >
                                          No items
                                        </td>
                                      </tr>
                                    ) : (
                                      salesLines.map((li, i) => (
                                        <tr
                                          key={i}
                                          className="hover:bg-gray-50/60 transition-colors"
                                        >
                                          <td className="py-3 px-4 text-gray-400">
                                            {i + 1}
                                          </td>
                                          <td className="py-3 px-4 text-gray-700">
                                            {li.Description ||
                                              li.SalesItemLineDetail?.ItemRef
                                                ?.name ||
                                              "—"}
                                          </td>
                                          <td className="py-3 px-4 text-right text-gray-600">
                                            {li.SalesItemLineDetail?.Qty ?? "—"}
                                          </td>
                                          <td className="py-3 px-4 text-right text-gray-600">
                                            {formatCurrency(
                                              li.SalesItemLineDetail?.UnitPrice,
                                              selectedInvoice.currencyCode,
                                            )}
                                          </td>
                                          <td className="py-3 px-4 text-right font-semibold text-gray-800">
                                            {formatCurrency(
                                              li.Amount,
                                              selectedInvoice.currencyCode,
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* ── Summary ── */}
                            <div className="px-5 py-4 border-b border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5">
                                Summary
                              </p>
                              <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
                                {/* Subtotal */}
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Subtotal
                                  </p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    {formatCurrency(
                                      subtotal,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>

                                {/* Tax */}
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">Tax</p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    {formatCurrency(
                                      tax,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Discount
                                  </p>
                                  <p className="text-sm font-semibold text-gray-700">
                                    -
                                    {formatCurrency(
                                      discount,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>

                                {/* Total Invoice */}
                                <div className="flex items-center justify-between px-4 py-3">
                                  <p className="text-sm text-gray-500">
                                    Invoice Total
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {formatCurrency(
                                      totalInvoice,
                                      selectedInvoice?.currencyCode,
                                    )}
                                  </p>
                                </div>

                                {/* Partial breakdown rows */}
                                {p.isPartial && !isPaid && (
                                  <>
                                    <div className="flex items-center justify-between px-4 py-3 bg-amber-50">
                                      <div>
                                        <p className="text-sm text-amber-700 font-medium">
                                          {p.partialPaid
                                            ? "Partial already paid"
                                            : `Step 1 — Pay now (${p.partialMode === "percentage" ? `${p.partialValue}%` : "fixed"})`}
                                        </p>
                                        {!p.partialPaid && (
                                          <p className="text-xs text-amber-600 mt-0.5">
                                            Due immediately
                                          </p>
                                        )}
                                      </div>
                                      <p className="text-sm font-bold text-amber-700">
                                        {formatCurrency(
                                          p.minimumPartialAmt,
                                          selectedInvoice?.currencyCode,
                                        )}
                                      </p>
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-3 bg-blue-50/60">
                                      <div>
                                        <p
                                          className={`text-sm font-medium ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                                        >
                                          {p.partialPaid
                                            ? "Step 2 — Remaining balance due"
                                            : "Remaining balance (locked)"}
                                        </p>
                                        {!p.partialPaid && (
                                          <p className="text-xs text-gray-400 mt-0.5">
                                            Unlocks after Step 1
                                          </p>
                                        )}
                                      </div>
                                      <p
                                        className={`text-sm font-bold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                                      >
                                        {formatCurrency(
                                          p.totalAmount - p.minimumPartialAmt,
                                          selectedInvoice?.currencyCode,
                                        )}
                                      </p>
                                    </div>
                                  </>
                                )}

                                {/* Paid breakdown row */}
                                {isPaid && (
                                  <div className="flex items-center justify-between px-4 py-3 bg-[#F0FEF4]">
                                    <p className="text-sm text-[#008236] font-medium">
                                      Total Paid
                                    </p>
                                    <p className="text-sm font-bold text-[#008236]">
                                      {formatCurrency(
                                        totalInvoice,
                                        selectedInvoice?.currencyCode,
                                      )}
                                    </p>
                                  </div>
                                )}

                                {/* Bottom total bar */}
                                <div className="flex items-center justify-between px-4 py-3.5 bg-[#115B9D] rounded-b-xl">
                                  <p className="text-sm font-bold text-white tracking-wide">
                                    {isPaid
                                      ? "Fully Paid ✓"
                                      : p.isPartial
                                        ? p.partialPaid
                                          ? "Balance Due (Step 2)"
                                          : "Due Now (Step 1)"
                                        : "Total Due"}
                                  </p>
                                  <p className="text-base font-bold text-white">
                                    {isPaid
                                      ? formatCurrency(
                                          totalInvoice,
                                          selectedInvoice?.currencyCode,
                                        )
                                      : formatCurrency(
                                          p.isPartial
                                            ? p.amountToPay
                                            : amountDueRaw,
                                          selectedInvoice?.currencyCode,
                                        )}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* ── Pay CTA ── */}
                            {!isPaid && (
                              <div className="mx-5 my-5 border-2 border-dashed border-green-300 rounded-2xl px-5 py-5 text-center bg-[#f0fdf4]">
                                <p className="text-[10px] font-bold tracking-[2px] uppercase text-green-700 mb-1">
                                  {p.isPartial
                                    ? p.partialPaid
                                      ? "Step 2 of 2 — Pay Remaining Balance"
                                      : "Step 1 of 2 — Partial Payment Required"
                                    : "Ready to Pay?"}
                                </p>
                                <p className="text-xs text-green-700/80 mb-4 leading-relaxed max-w-sm mx-auto">
                                  {p.isPartial
                                    ? p.partialPaid
                                      ? `Your partial payment of ${formatCurrency(p.minimumPartialAmt)} was received. Pay the remaining ${formatCurrency(p.totalAmount - p.minimumPartialAmt)} to fully clear this invoice.`
                                      : `A minimum of ${formatCurrency(p.minimumPartialAmt)} (${p.partialMode === "percentage" ? `${p.partialValue}% of ${formatCurrency(p.totalAmount)}` : "fixed amount"}) is required now. The remaining ${formatCurrency(p.totalAmount - p.minimumPartialAmt)} can be paid in Step 2.`
                                    : "Click below to complete your payment securely."}
                                </p>

                                <button
                                  type="button"
                                  onClick={() => handlePayment(selectedInvoice)}
                                  disabled={
                                    paymentLoading[selectedInvoice?.invoiceId]
                                  }
                                  className="inline-flex items-center gap-2 bg-[#008236] hover:bg-[#016f2f] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm px-7 py-3 rounded-full transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                  {paymentLoading[
                                    selectedInvoice?.invoiceId
                                  ] ? (
                                    <>
                                      <SpinIcon /> Processing…
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <rect
                                          x="1"
                                          y="11"
                                          width="22"
                                          height="11"
                                          rx="2"
                                          ry="2"
                                        />
                                        <path d="M1 11V8a2 2 0 0 1 2-2h4l2-4 2 4h4a2 2 0 0 1 2 2v3" />
                                      </svg>
                                      {p.isPartial
                                        ? `Pay ${p.partialPaid ? "Balance" : "Step 1"} — ${formatCurrency(p.amountToPay, selectedInvoice?.currencyCode)}`
                                        : `Pay Now — ${formatCurrency(amountDueRaw, selectedInvoice?.currencyCode)}`}
                                    </>
                                  )}
                                </button>
                                {paymentError[selectedInvoice?.invoiceId] && (
                                  <p className="mt-2 text-xs text-red-600">
                                    {paymentError[selectedInvoice?.invoiceId]}
                                  </p>
                                )}
                                <div className="flex justify-center items-center gap-2 mt-2.5">
                                  <span className="text-[10px] text-[#008236]">
                                    🔒 Secure payment
                                  </span>
                                  <span className="text-green-300">·</span>
                                  <span className="text-[10px] text-[#008236]">
                                    Your data is protected
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* ── Addresses ── */}
                            <div className="flex gap-4 px-5 pb-5">
                              {[
                                {
                                  title: "📍 Billing Address",
                                  addr: selectedInvoice?.raw?.BillAddr,
                                },
                                {
                                  title: "🚚 Shipping Address",
                                  addr: selectedInvoice?.raw?.ShipAddr,
                                },
                              ].map(({ title, addr }) => (
                                <div
                                  key={title}
                                  className="flex-1 bg-white border border-[#115B9D]/30 p-4 rounded-xl"
                                >
                                  <h2 className="text-xs font-semibold text-[#115B9D] border-b border-[#115B9D]/30 pb-2 mb-2 uppercase tracking-wide">
                                    {title}
                                  </h2>
                                  <div className="space-y-0.5 text-sm text-gray-600">
                                    {[
                                      addr?.Line1,
                                      addr?.City || addr?.Line2,
                                      addr?.State,
                                      addr?.Country || addr?.Line3,
                                      addr?.PostalCode,
                                    ]
                                      .filter(Boolean)
                                      .map((line, i) => (
                                        <p key={i}>{line}</p>
                                      ))}
                                    {!addr?.Line1 && (
                                      <p className="text-gray-400 text-xs">
                                        No address on file
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                  </div>

                  {/* ── Footer ── */}
                  <div className="bg-[#115B9D] px-5 py-3 shrink-0">
                    <p className="text-center text-[11px] text-white">
                      © 2026 · All rights reserved
                    </p>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
